import {useEffect} from 'react';
import type {AiChatHostMessage, RegisterAiChatControls, RequestConfig} from '@Components/ai-chat-window/ai-chat-window.types';
import {createAiChatControls} from '@Components/ai-chat-window/lib/ai-chat-controls.library';
import {buildRequestConfig} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import type {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {useLatestRef} from '@Hooks/useLatestRef';

interface UseAiChatHostControlsParams {
  registerControls?: RegisterAiChatControls;
  promptBoxId: string;
  isHistoryLoaded: boolean;
  onGenerate: (prompt: string, promptAttachments?: AiPromptBoxAttachments, requestConfig?: RequestConfig) => Promise<void>;
  onStop: () => Promise<void>;
}

export function useAiChatHostControls({registerControls, promptBoxId, isHistoryLoaded, onGenerate, onStop}: UseAiChatHostControlsParams): void {
  const generateRef = useLatestRef(onGenerate);
  const stopRef = useLatestRef(onStop);

  useEffect(() => {
    if (!registerControls || !isHistoryLoaded) {
      return undefined;
    }

    registerControls(
      createAiChatControls({
        promptBoxId,
        sendMessage: (message: AiChatHostMessage): Promise<void> => {
          return generateRef.current(message.prompt, message.attachments, buildRequestConfig({preferences: message.preferences}));
        },
        stopTurn: (): Promise<void> => {
          return stopRef.current();
        },
      })
    );

    return (): void => {
      registerControls(null);
    };
  }, [registerControls, promptBoxId, isHistoryLoaded]);
}
